<template>
  <div class="h-full bg-base-200 flex flex-col overflow-hidden">
    <!-- Header -->
    <threads-nav show-back>
      <template #center> 开通会员 </template>
    </threads-nav>

    <!-- Main Content -->
    <div v-if="loading" class="flex-1 flex justify-center items-center">
      <div class="loading loading-ring loading-lg"></div>
    </div>
    <main v-else class="flex-1 overflow-hidden flex flex-col">
      <!-- Plan Toggle -->
      <van-tabs v-model:active="currentPlan" @change="clickToPlan(currentPlan)">
        <van-tab title="小镇规划师" :name="MemberType.Basic"></van-tab>
        <van-tab title="行星构筑师" :name="MemberType.Standard"></van-tab>
        <van-tab title="星系幻想家" :name="MemberType.Premium"></van-tab>
      </van-tabs>

      <div class="flex-1 overflow-y-auto p-4">
        <!-- Pricing Options -->
        <div class="grid grid-cols-3 gap-3 text-center">
          <div
            v-for="(price, index) in currentPlanData.prices"
            :key="index"
            :class="[
              'card relative bg-base-100 cursor-pointer border-2 ',
              selectedPrice.id === price.id
                ? 'border-primary'
                : 'border-base-200',
            ]"
            @click="clickPlan(price)"
          >
            <div
              v-if="price.tag"
              :class="[
                'badge absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap',
                price.tag === '最划算' ? 'badge-secondary' : 'badge-primary',
              ]"
            >
              {{ price.tag }}
            </div>
            <div class="card-body p-3 pt-5">
              <h3 class="font-semibold">{{ price.type }}</h3>
              <p
                :class="[
                  'text-2xl font-bold my-1',
                  { 'text-primary': selectedPrice.id === price.id },
                ]"
              >
                <span class="text-lg">¥</span>{{ price.price }}
              </p>
            </div>
          </div>
        </div>

        <div class="my-4 flex justify-center items-center">
          <div
            class="flex justify-center items-center gap-1 bg-neutral text-neutral-content px-4 py-1 rounded-full"
          >
            会员期间可共计获得 <PowerIcon />
            <span class="text-primary text-base font-bold">
              {{ fixNumber(currentPlanData.total * month) }}
            </span>
          </div>
        </div>
        <!-- Benefits Section -->
        <div class="bg-base-100 rounded-box p-5">
          <h2 class="text-lg font-semibold mb-4 text-content-focus">
            {{ currentPlanData.name }} - 每月会员权益
          </h2>

          <CrystalMemberBenefits
            :benefits="currentPlanData?.benefits"
            :month="month"
          />
        </div>

        <CrystalPayment
          v-model:selected-payment-method="selectedPaymentMethod"
          v-model:selected-price="selectedPrice"
        />
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { readMemberPlanPage } from "@/apis/user";
import { BillingCycle, MemberType, PaymentMethod } from "@/enums/mateEnums";
import { originPlanData } from "./memberData";
import { fixNumber } from "@/components/Mission/MissionStat";
import { sessionStorageGet, sessionStorageSet } from "@/tools/sessionStorage";
import { routerBack } from "@/use/go";

const plansData = ref(originPlanData);
const loading = ref(true);

// 响应式状态
const currentPlan = ref<
  MemberType.Standard | MemberType.Premium | MemberType.Basic
>(MemberType.Standard);
const selectedPaymentMethod = ref<PaymentMethod>(PaymentMethod.Crystal);
const selectedPrice = ref<{
  id: number;
  price: number;
  billingCycle: BillingCycle;
  wechatPlanId?: number;
}>({ id: 0, price: 0, billingCycle: BillingCycle.Month });

const month = computed(() => {
  if (selectedPrice.value.billingCycle === BillingCycle.Quarter) return 3;
  if (selectedPrice.value.billingCycle === BillingCycle.Year) return 12;
  return 1;
});

// 计算属性
const currentPlanData = ref(originPlanData[currentPlan.value]);

const clickToPlan = (
  plan: MemberType.Standard | MemberType.Premium | MemberType.Basic
) => {
  currentPlan.value = plan;
  currentPlanData.value = originPlanData[plan];
  selectedPrice.value = currentPlanData.value.prices[0];
  selectedPaymentMethod.value = PaymentMethod.Crystal;
  sessionStorageSet("memberPlan", plan);
};

const clickPlan = (price: {
  id: number;
  price: number;
  billingCycle: BillingCycle;
}) => {
  if (currentPlanData.value.prices.find((v) => v.id === price.id)) {
    selectedPrice.value = price;
    selectedPaymentMethod.value = PaymentMethod.Crystal;
    sessionStorageSet("memberPrice", price);
  }

  // if (userStore.user.crystalQuantity < price.price) {
  //   selectedPaymentMethod.value = PaymentMethod.Wechat;
  // }
};

onMounted(async () => {
  try {
    const res = await readMemberPlanPage();
    if (res.page.data.length < 9) return;
    const [
      monthStandard,
      quarterStandard,
      yearStandard,
      monthPremium,
      quarterPremium,
      yearPremium,
      monthBasic,
      quarterBasic,
      yearBasic,
    ] = res.page.data;

    plansData.value[MemberType.Standard].prices = [
      { type: "连续包月", tag: "超值首选", ...monthStandard },
      { type: "连续包季", tag: "", ...quarterStandard },
      { type: "连续包年", ...yearStandard, tag: "最划算" },
    ];

    plansData.value[MemberType.Premium].prices = [
      { type: "连续包月", tag: "超值首选", ...monthPremium },
      { type: "连续包季", tag: "", ...quarterPremium },
      { type: "连续包年", tag: "最划算", ...yearPremium },
    ];

    plansData.value[MemberType.Basic].prices = [
      { type: "连续包月", tag: "超值首选", ...monthBasic },
      { type: "连续包季", tag: "", ...quarterBasic },
      { type: "连续包年", tag: "最划算", ...yearBasic },
    ];

    const plan = sessionStorageGet("memberPlan");
    if (plan) {
      clickToPlan(plan);
    } else {
      clickToPlan(MemberType.Standard);
    }
    const price = sessionStorageGet("memberPrice");
    if (price) {
      clickPlan(price);
    }
    loading.value = false;
  } catch (e) {
    console.log(e);
    routerBack();
  }
});
</script>
